import type { BillingInfo } from '@ctct/domain-layer';
import { envs } from '@ctct/infrastructure-layer';
import type { PaymentMethodsPageStore } from '@ctct/state-client-layer';
import type { CreateAmazonPaySessionMutation, CreateAmazonPaySignatureMutation } from '@ctct/state-server-layer';

import { paymentMethodsEventBus } from '../PaymentMethodsEventBus';
import type { PaymentMethodsUrlAdapter } from '../types';

export class AmazonPayService {
  private readonly AMAZON_BUTTON_ID = 'AmazonPayButton';

  constructor(
    private pageStore: PaymentMethodsPageStore,
    private billingInfo: BillingInfo,
    private createAmazonPaySignatureMutation: CreateAmazonPaySignatureMutation,
    private createAmazonPaySessionMutation: CreateAmazonPaySessionMutation,
    private urlAdapter: PaymentMethodsUrlAdapter,
    private isNewPayflow: boolean,
    private isNewCustomerPayflow: boolean
  ) {}

  public data() {
    return this.getterObject;
  }

  public handlers() {
    return this.handlersObject;
  }

  private readonly getterObject = {
    getAmazonButtonId: () => this.AMAZON_BUTTON_ID,
    getAmazonPayCheckoutSession: () => this.pageStore.getState().amazonPayCheckoutSession,
  };

  private readonly handlersObject = {
    renderAmazonButton: async () => {
      const AMAZON_MERCHANT_ID = 'A24DMWEUMJX308';
      const publicKeyId = 'AGGMY2UZ3HPZSE2HN72YQCVD';
      const storeId = 'amzn1.application-oa2-client.2e6afc2b646e4ab68c81fa4947603586';

      // setting to localhost for test environment, didn't manage to mock it from test file
      const url = new URL(window.location.href ?? 'http://localhost');

      if (this.isNewPayflow) {
        url.searchParams.set('aff-trialer-payflow', 'true');
      }

      if (this.isNewCustomerPayflow) {
        url.searchParams.set('aff-customer-payflow', 'true');
      }

      const returnUrl = url.href;

      const payload = {
        chargePermissionType: 'Recurring',
        paymentDetails: {
          paymentIntent: 'Confirm',
        },
        recurringMetadata: {
          frequency: {
            unit: 'Month',
            value: `${this.billingInfo.getPaymentPeriod()}`,
          },
        },
        storeId,
        webCheckoutDetails: {
          checkoutResultReturnUrl: returnUrl,
          checkoutReviewReturnUrl: returnUrl,
        },
      };

      const signature = await this.createAmazonPaySignatureMutation.mutateAsync(payload);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.amazon.Pay.renderButton(`#${this.AMAZON_BUTTON_ID}`, {
        merchantId: AMAZON_MERCHANT_ID,
        ledgerCurrency: 'USD',
        sandbox: !envs.isProd(),
        checkoutLanguage: 'en_US',
        productType: 'PayOnly',
        placement: 'Cart',
        createCheckoutSessionConfig: {
          payloadJSON: JSON.stringify(payload),
          signature,
          publicKeyId,
        },
      });
    },

    handleAmazonToken: async () => {
      const { getAmazonCheckoutSessionId, deleteAmazonCheckoutSessionId } =
        this.urlAdapter.getPaymentMethodsUrlParams();

      const amazonToken = getAmazonCheckoutSessionId();

      if (!amazonToken) {
        return;
      }

      const response = await this.createAmazonPaySessionMutation.mutateAsync(amazonToken);

      if (response.webCheckoutDetails?.amazonPayRedirectUrl) {
        window.location.replace(response.webCheckoutDetails.amazonPayRedirectUrl);

        return;
      }

      this.pageStore.setState({ amazonPayCheckoutSession: response });

      deleteAmazonCheckoutSessionId();
      paymentMethodsEventBus.emit('amazonPayConnected', response, { source: 'onMount' });
    },

    validateAmazonPay: () => {
      const amazonToken =
        this.pageStore.getState().amazonPayCheckoutSession?.amazonPayToken ??
        this.billingInfo.getAmazonPayInfo()?.token;

      if (!amazonToken) {
        throw new Error('Please setup your Amazon pay account.');
      }

      return {
        token: amazonToken,
      };
    },
  };
}
